/**
 * File Dialog — simulated Windows Explorer "Open / Save" dialog
 * Students never access the real filesystem; this dialog shows a
 * pre-populated fake directory tree from fakeFileSystem.ts.
 *
 * Reference: assets/screenshots/SV Windows Explorer.png
 */
import { getFsNode, getChildren, matchesFilter, getSidebarItems, type FsNode } from '../data/fakeFileSystem';
import { enterpriseStore } from '../stores/enterpriseStore';

export interface FileDialogConfig {
  title: string;
  startPath: string;
  filters: string[];           // e.g. ['SVG Files (*.svg)', 'All Files (*.*)']
  mode: 'open' | 'save';
  onConfirm: (file: FsNode | null, fileName: string) => void;
  defaultFileName?: string;
}

// Lesson-specific file restrictions: only these files are clickable during certain lessons
const LESSON_ALLOWED_FILES: Record<string, string[]> = {
  'ent-gfx-01': ['Atlas_Pet_Vets.png', 'Andrews_Commercial_2021.webm'],
};

function getAllowedFiles(): string[] | null {
  const lessonId = enterpriseStore.getState().activeLessonId;
  if (!lessonId) return null;
  return LESSON_ALLOWED_FILES[lessonId] ?? null;
}

// ── State ─────────────────────────────────────────────────────

let overlay: HTMLElement | null = null;
let currentPath = '';
let selectedNode: FsNode | null = null;
let activeFilter = '';
let config: FileDialogConfig | null = null;

// ── Public API ────────────────────────────────────────────────

export function openFileDialog(cfg: FileDialogConfig): void {
  if (overlay) return;       // one at a time
  config = cfg;
  currentPath = cfg.startPath;
  selectedNode = null;
  activeFilter = cfg.filters[0] || '*.*';

  buildDialog();
}

// ── Build ─────────────────────────────────────────────────────

function buildDialog(): void {
  if (!config) return;
  overlay = document.createElement('div');
  overlay.className = 'fd-overlay';
  overlay.innerHTML = `
    <div class="fd-dialog">
      <div class="fd-titlebar">
        <span class="fd-titlebar-text">${escHtml(config.title)}</span>
        <button class="fd-close">&times;</button>
      </div>
      <div class="fd-toolbar">
        <span class="fd-toolbar-btn">Organize &#x25BE;</span>
        <span class="fd-toolbar-btn">New folder</span>
      </div>
      <div class="fd-breadcrumb" id="fd-breadcrumb"></div>
      <div class="fd-content">
        <div class="fd-sidebar" id="fd-sidebar"></div>
        <div class="fd-file-area" id="fd-file-area"></div>
      </div>
      <div class="fd-footer">
        <div class="fd-footer-row">
          <label class="fd-footer-label">File name:</label>
          <input class="fd-filename-input" id="fd-filename" type="text" value="${escAttr(config.defaultFileName || '')}" />
        </div>
        <div class="fd-footer-row">
          <label class="fd-footer-label">File type:</label>
          <select class="fd-filter-select" id="fd-filter">
            ${config.filters.map((f) => `<option value="${escAttr(f)}">${escHtml(f)}</option>`).join('')}
          </select>
        </div>
        <div class="fd-btn-row">
          <button class="fd-btn fd-btn-open" id="fd-open-btn">${config.mode === 'save' ? 'Save' : 'Open'}</button>
          <button class="fd-btn fd-btn-cancel">Cancel</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  renderSidebar();
  renderBreadcrumb();
  renderFileArea();
  attachHandlers();
}

// ── Render helpers ────────────────────────────────────────────

const SIDEBAR_ICONS: Record<string, string> = {
  'Quick access': '\u2B50',       // star
  'Desktop': '\uD83D\uDDA5',      // desktop
  'Downloads': '\u2B07',          // down arrow
  'Documents': '\uD83D\uDCC4',    // document
  'Pictures': '\uD83D\uDDBC',     // framed picture
  'Videos': '\uD83C\uDFAC',       // movie clapper
  'OneDrive - Personal': '\u2601', // cloud
  'This PC': '\uD83D\uDCBB',      // laptop
  'Network': '\uD83C\uDF10',      // globe
};

function renderSidebar(): void {
  const sidebar = overlay?.querySelector('#fd-sidebar');
  if (!sidebar) return;
  const items = getSidebarItems();
  sidebar.innerHTML = items.map((item) => {
    const icon = SIDEBAR_ICONS[item.name] || '\uD83D\uDCC1';
    const isActive = currentPath.startsWith(item.path);
    return `<div class="fd-sidebar-item${isActive ? ' active' : ''}" data-path="${escAttr(item.path)}">
      <span class="fd-sidebar-icon">${icon}</span>
      <span>${escHtml(item.name)}</span>
    </div>`;
  }).join('');
}

function renderBreadcrumb(): void {
  const bar = overlay?.querySelector('#fd-breadcrumb');
  if (!bar) return;

  const segments = currentPath.split('/');
  let html = '';
  let accum = '';
  for (let i = 0; i < segments.length; i++) {
    if (i > 0) accum += '/';
    accum += segments[i];
    if (i > 0) html += `<span class="fd-crumb-sep">&#x25B8;</span>`;
    html += `<span class="fd-crumb" data-path="${escAttr(accum)}">${escHtml(segments[i])}</span>`;
  }
  bar.innerHTML = html;
}

function renderFileArea(): void {
  const area = overlay?.querySelector('#fd-file-area');
  if (!area) return;

  const children = getChildren(currentPath).filter((n) => matchesFilter(n, activeFilter));

  if (children.length === 0) {
    area.innerHTML = `<div class="fd-empty">This folder is empty</div>`;
    return;
  }

  // Folders first, then files, alphabetical within each group
  const sorted = [...children].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  const allowed = getAllowedFiles();
  area.innerHTML = sorted.map((node) => {
    const isSelected = selectedNode?.path === node.path;
    const isRestricted = allowed !== null && node.type === 'file' && !allowed.includes(node.name);
    const thumb = getThumbHtml(node);
    return `<div class="fd-file-item${isSelected ? ' selected' : ''}${isRestricted ? ' fd-restricted' : ''}" data-path="${escAttr(node.path)}" data-type="${node.type}">
      <div class="fd-file-thumb">${thumb}</div>
      <div class="fd-file-name">${escHtml(node.name)}</div>
    </div>`;
  }).join('');
}

function getThumbHtml(node: FsNode): string {
  if (node.type === 'folder') {
    return `<span class="fd-icon" style="color:#e8a735;">\uD83D\uDCC1</span>`;
  }
  if (node.thumbUrl) {
    return `<img src="${escAttr(node.thumbUrl)}" alt="${escAttr(node.name)}" />`;
  }
  if (node.assetUrl && node.fileType === 'image') {
    return `<img src="${escAttr(node.assetUrl)}" alt="${escAttr(node.name)}" />`;
  }
  if (node.fileType === 'svg') {
    return `<span class="fd-icon" style="color:#6ec6ff;">\uD83D\uDCC4</span>`;
  }
  return `<span class="fd-icon" style="color:#999;">\uD83D\uDCC4</span>`;
}

// ── Handlers ──────────────────────────────────────────────────

function attachHandlers(): void {
  if (!overlay || !config) return;

  // Close button
  overlay.querySelector('.fd-close')?.addEventListener('click', close);

  // Cancel button
  overlay.querySelector('.fd-btn-cancel')?.addEventListener('click', close);

  // Overlay background click
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('fd-overlay')) close();
  });

  // Escape
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  // Open/Save button
  overlay.querySelector('#fd-open-btn')?.addEventListener('click', handleConfirm);

  // Filter dropdown
  overlay.querySelector<HTMLSelectElement>('#fd-filter')?.addEventListener('change', (e) => {
    activeFilter = (e.target as HTMLSelectElement).value;
    selectedNode = null;
    renderFileArea();
    reAttachFileHandlers();
  });

  // Sidebar items
  attachSidebarHandlers();

  // Breadcrumb items
  attachBreadcrumbHandlers();

  // File area items
  reAttachFileHandlers();

  // Enter key in filename input
  overlay.querySelector('#fd-filename')?.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') handleConfirm();
  });
}

function attachSidebarHandlers(): void {
  overlay?.querySelectorAll<HTMLElement>('.fd-sidebar-item').forEach((el) => {
    el.addEventListener('click', () => {
      const path = el.dataset.path!;
      navigateTo(path);
    });
  });
}

function attachBreadcrumbHandlers(): void {
  overlay?.querySelectorAll<HTMLElement>('.fd-crumb').forEach((el) => {
    el.addEventListener('click', () => {
      const path = el.dataset.path!;
      navigateTo(path);
    });
  });
}

function reAttachFileHandlers(): void {
  overlay?.querySelectorAll<HTMLElement>('.fd-file-item').forEach((el) => {
    const path = el.dataset.path!;
    const type = el.dataset.type!;

    const restricted = el.classList.contains('fd-restricted');

    // Single click — select
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      if (restricted) return;
      if (type === 'file') {
        const node = getFsNode(path);
        if (node) {
          selectedNode = node;
          const fnInput = overlay?.querySelector<HTMLInputElement>('#fd-filename');
          if (fnInput) fnInput.value = node.name;
        }
      } else {
        selectedNode = null;
      }
      // Update visual selection
      overlay?.querySelectorAll('.fd-file-item.selected').forEach((s) => s.classList.remove('selected'));
      el.classList.add('selected');
    });

    // Double click — folder navigates in, file opens
    el.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (restricted) return;
      if (type === 'folder') {
        navigateTo(path);
      } else {
        // Double-click file = confirm
        const node = getFsNode(path);
        if (node) {
          selectedNode = node;
          handleConfirm();
        }
      }
    });
  });
}

function navigateTo(path: string): void {
  currentPath = path;
  selectedNode = null;
  renderSidebar();
  renderBreadcrumb();
  renderFileArea();
  attachSidebarHandlers();
  attachBreadcrumbHandlers();
  reAttachFileHandlers();
}

function handleConfirm(): void {
  if (!config) return;
  const fnInput = overlay?.querySelector<HTMLInputElement>('#fd-filename');
  const fileName = fnInput?.value.trim() || '';

  if (config.mode === 'open' && !selectedNode) {
    // If they typed a filename but didn't select, try to find it
    if (fileName) {
      const children = getChildren(currentPath);
      const match = children.find((n) => n.name.toLowerCase() === fileName.toLowerCase());
      if (match && match.type === 'file') {
        selectedNode = match;
      }
    }
    if (!selectedNode) return; // nothing to open
  }

  config.onConfirm(selectedNode, fileName);
  close();
}

function close(): void {
  if (overlay) {
    overlay.remove();
    overlay = null;
  }
  config = null;
  selectedNode = null;
}

// ── Escape utilities ──────────────────────────────────────────

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function escAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
